import React, { useEffect, useState } from 'react'
import Modal from '../Modal'
import Button from '../../ui/buttons/Button'
import Input from '../../ui/inputs/Input'
import CurrencyInput from '../../ui/inputs/CurrencyInput'
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox'
import Select from '../../ui/selects/Select'
import Textarea from '../../ui/inputs/Textarea'
import { Car, X } from 'lucide-react'
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf'
import { snakecaseKeys } from '../../../utils/snakecaseKeys'
import { camelizeKeys } from '../../../utils/camelizeKeys'
import { useToast } from '../../ui/toast/ToastContext'

const VehicleModal = ({ clientId, vehicleId = null, scope, onClose, onSuccess }) => {
  const isEdit = !!vehicleId
  const { addToast } = useToast()

  const [form, setForm] = useState({
    name: '',
    vehicleType: '',
    value: '',
    isPersonal: false,
    notes: ''
  })

  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([])

  // Carrega dados do veículo (modo edição)
  useEffect(() => {
    if (isEdit) {
      fetch(`/app/planner/client/${clientId}/dashboard/properties/vehicles/${vehicleId}?scope=${scope}`)
        .then(res => res.json())
        .then(data => setForm(camelizeKeys(data)))
        .catch(err => {
          console.error('Erro ao carregar veículo', err)
          addToast({ type: 'error', message: 'Erro ao carregar dados do veículo' })
        })
    }
  }, [isEdit, clientId, vehicleId])

  // Carrega options do select
  useEffect(() => {
    fetch('/app/shared/form_options')
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        setVehicleTypeOptions(camelData.vehicleTypes.map(type => ({
          label: type.label,
          value: type.value
        })))
      })
      .catch(err => {
        console.error('Erro ao carregar dados do formulário', err)
        addToast({ type: 'error', message: err.message })
      })
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleCheckbox = (name) => {
    setForm(prev => ({ ...prev, [name]: !prev[name] }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const payload = snakecaseKeys({ vehicleProperty: form })

    const url = isEdit
      ? `/app/planner/client/${clientId}/dashboard/properties/vehicles/${vehicleId}?scope=${scope}`
      : `/app/planner/client/${clientId}/dashboard/properties/vehicles?scope=${scope}`

    const method = isEdit ? 'PATCH' : 'POST'
    const successMsg = isEdit ? 'Veículo atualizado com sucesso!' : 'Veículo cadastrado com sucesso!'

    try {
      const res = await fetchWithCsrf(url, {
        method,
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      if (!res.ok) {
        const json = await res.json()
        throw new Error(json.message || 'Erro ao salvar veículo')
      }

      onClose()
      addToast({ type: 'success', message: successMsg })
      onSuccess()
    } catch (err) {
      addToast({ type: 'error', message: err.message })
    }
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <Car size={24} className="text-tealMedium" strokeWidth={1.25} />
            {isEdit ? 'Editar Veículo' : 'Cadastrar Veículo'}
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <Input name="name" label="Identificação" value={form.name} onChange={handleChange} required />
          <Select name="vehicleType" label="Tipo do veículo" value={form.vehicleType} onChange={handleChange} options={vehicleTypeOptions} required />
          <div className='col-span-2'>
            <CurrencyInput name="value" label="Valor de mercado" value={form.value} onChange={handleChange} />
          </div>

          <Textarea
            name="notes"
            label="Observações"
            value={form.notes}
            onChange={handleChange}
            className="md:col-span-2"
            rows={4}
          />

          <CustomCheckbox
            name="isPersonal"
            label="É um bem particular"
            checked={form.isPersonal}
            onChange={() => handleCheckbox('isPersonal')}
            labelClassName='text-base'
          />

          <div className="md:col-span-2 flex justify-end items-center mt-1">
            <Button type="submit">
              {isEdit ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR VEÍCULO'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default VehicleModal
